import { type MeditationDailyCMS } from "@repo/common/src/types/firestore/cms/meditationDaily";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  type EntityOnSaveProps,
  type EntityReference,
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
} from "@firecms/core";

import { standardPermissions } from "../../utils/constants";
import { fileOperationsMeditation } from "../../utils/fileOperationsMeditation";
import { generateUniqueSlug } from "../../utils/generateUniqueSlug";
import { loadingOverlay } from "../../utils/loadOverlay";
import { Groups } from "../types";
import { FrontendError, FrontendErrorCodesCms } from "@repo/common";

type MeditationDailyCMSModel = Omit<MeditationDailyCMS, "author"> & { author: EntityReference };

const callbacks = buildEntityCallbacks({
  onPreSave: async (props: EntityOnSaveProps<MeditationDailyCMSModel>) => {
    const { values, entityId } = props;

    loadingOverlay.show();

    if (values.name) {
      const baseSlug = values.slug || values.name;
      values.slug = await generateUniqueSlug(getFirestore(), CMSCollections.MeditationDailies, baseSlug, entityId);
    }

    if (!values.meditation) {
      return values;
    }

    const storage = getStorage();

    let updatedMeditation: Awaited<ReturnType<typeof fileOperationsMeditation>>;
    try {
      updatedMeditation = await fileOperationsMeditation({
        file: values.meditation,
        storage,
        isPremium: values.premium ?? false,
      });
    } catch (error: unknown) {
      if (error instanceof FrontendError) {
        throw error;
      }
      throw new FrontendError(
        "Audio konnte nicht verarbeitet werden. Bitte Datei prüfen und erneut versuchen.",
        FrontendErrorCodesCms.AudioProcessingFailed,
        "danger",
      );
    }

    return { ...values, meditation: updatedMeditation };
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

export const meditationDailies = buildCollection<MeditationDailyCMSModel>({
  id: CMSCollections.MeditationDailies,
  name: "Tagesmeditationen",
  singularName: "Tagesmeditation",
  path: CMSCollections.MeditationDailies,
  icon: "Audiotrack",
  group: Groups.Meditations,
  permissions: () => {
    return standardPermissions;
  },
  callbacks,
  properties: {
    published: {
      name: "Veröffentlicht",
      dataType: "boolean",
      description: "Soll der Kurs auf der App sichtbar sein?",
    },
    premium: {
      name: "Premium",
      dataType: "boolean",
      description: "Soll die Meditation nur für Premium Nutzer sichtbar sein?",
    },
    name: {
      name: "Name",
      validation: { required: true },
      dataType: "string",
    },
    slug: {
      name: "URL-freundlicher Slug. Wird automatisch generiert, wenn leer.",
      dataType: "string",
    },
    main_image: buildProperty({
      name: "Kursbild",
      description: "Bild des Kurses. Bitte in 1024x1024 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.MeditationDailiesImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxHeight: 1024,
          maxWidth: 1024,
        },
      },
    }),
    description: {
      name: "Beschreibung",
      multiline: true,
      dataType: "string",
      validation: { required: true },
      columnWidth: 300,
    },
    author: {
      dataType: "reference",
      name: "Autor",
      validation: { required: true },
      path: CMSCollections.Authors,
    },
    date: {
      name: "Veröffentlichungsdatum",
      validation: { required: true },
      dataType: "date",
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
    meditation: {
      dataType: "map",
      name: "Meditation",
      properties: {
        closing: {
          name: "Schlusstext",
          multiline: true,
          dataType: "string",
          validation: { required: true },
          columnWidth: 300,
        },
        file: {
          name: "File",
          dataType: "string",
          validation: { required: true, uniqueInArray: true },
          storage: {
            storagePath: StoragePaths.MeditationDailiesFree,
            acceptedFiles: ["audio/*"],
            maxSize: 100 * 1024 * 1024, // 100 MB in bytes
          },
        },
      },
    },
  },
});
